import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Form,
    FormGroup,
    Label,
    InputGroup,
    InputGroupText,
    InputGroupAddon,
    FormFeedback
} from 'reactstrap'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from 'react-query';
import {toast} from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { passwordChange, respondChallenge } from '../api/actions/auth'
import { fetcher } from '../api/client'
import InputWithLoader from './InputWithLoader';
import LoadingButton from './LoadingButton'
import Alert from './Alert'
import useQueryParams from '../hooks/useQuery'
import { useAuthDispatch } from '../context/auth'
import { parseAuthResult } from './LoginForm'


const defaultValues = {
    password: '',
    confirmPassword: ''
}

const PasswordChangeSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, 'ShortPassword')
      .matches(/[a-z]/, 'MissingLowerCase')
      .matches(/[A-Z]/, 'MissingUpperCase')
      .required('Required'),
    confirmPassword: Yup.string()
      .min(8, 'ShortPassword')
      .matches(/[a-z]/, 'MissingLowerCase')
      .matches(/[A-Z]/, 'MissingUpperCase')
      .required('Required'), 
});

function PasswordChangeForm({formik, error, onErrorClose, errorMsg}) {
    const { t } = useTranslation('common')
    return (
        <>
        <Alert primary="ERROR" isOpen={error} toggle={onErrorClose} secondary={t(`errors.${errorMsg}`)} icon="info" size="sm" />
        <Form onSubmit={formik.handleSubmit} noValidate>
            <FormGroup>
                <Label for="password">{t('forms.newPassword')} <span className="text-accent">*</span></Label>
                <InputGroup className="mb-2">
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                            <FontAwesomeIcon icon="lock" className="text-primary mx-auto" />
                        </InputGroupText>
                    </InputGroupAddon>
                    <InputWithLoader 
                        type="password" 
                        id="password" 
                        name="password"
                        placeholder={t('forms.password')}
                        autoComplete="new-password"
                        loading={formik.isSubmitting}
                        onChange={formik.handleChange}
                        value={formik.values.password} 
                        invalid={formik.errors.password && formik.touched.password}
                    />
                    <FormFeedback>{t(`errors.${formik.errors.password}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
            <FormGroup>
                <Label for="">{t('forms.confirmNewPassword')} <span className="text-accent">*</span></Label>
                <InputGroup className="mb-2">
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                            <FontAwesomeIcon icon="lock" className="text-primary mx-auto" />
                        </InputGroupText>
                    </InputGroupAddon>
                    <InputWithLoader 
                        type="password" 
                        id="confirmPassword" 
                        placeholder={t('forms.confirmPassword')} 
                        autoComplete="new-password" 
                        name="confirmPassword"
                        loading={formik.isSubmitting}
                        onChange={formik.handleChange}
                        value={formik.values.confirmPassword} 
                        invalid={formik.errors.confirmPassword && formik.touched.confirmPassword}   
                    />
                    <FormFeedback>{t(`errors.${formik.errors.confirmPassword}`)}</FormFeedback>
                </InputGroup>
            </FormGroup>
            <div className="mt-4">
                <LoadingButton 
                        className="animate-up w-100 mb-3"
                        color="primary" 
                        type="submit" 
                        loadingText={t('actions.submitting')}
                        loading={formik.isSubmitting}
                    >
                    {t('auth.changePassword')}
                </LoadingButton>
            </div>
        </Form>
        </>
    );
}

function PasswordChangeFormContainer({initialValues = defaultValues}) {
    const history = useHistory();
    const { code, username } = useParams();
    const { t } = useTranslation('common')
    const qs = useQueryParams();
    const setAuth = useAuthDispatch()
    
    const { mutate: mutatePasswordChange, ...passwordChangeState } = useMutation((payload) => fetcher(passwordChange(payload)), {
        onSuccess: () => {
            history.push("/login", {
                showVerificationMessage: true
            })
            toast.info(t('successMessages.passwordChanged'))
        }
    });
    const { mutate: mutateRespondChallenge, ...respondChallengeState } = useMutation((payload) => fetcher(respondChallenge(payload)), {
        onSuccess: ({data}) => {
            setAuth(parseAuthResult(data));
            history.push("/");
        }
    });

    const formik = useFormik({
        initialValues,
        validationSchema: PasswordChangeSchema,
        onSubmit: async (formValues, actions) => {
            if (formValues.confirmPassword !== formValues.password) {
                actions.setFieldError('password', 'PasswordMustMatched')
                actions.setFieldError('confirmPassword', 'PasswordMustMatched')
            } else {
                if (qs.get('hasTmpPwd') === 'true') {
                    await mutateRespondChallenge({
                        type: 'NEW_PASSWORD_REQUIRED',
                        password: formValues.password,
                        session: code,
                        username,
                    });
                } else {
                    await mutatePasswordChange({
                        password: formValues.password,
                        username,
                        code
                    });
                }
               
               
            }
           
            actions.setSubmitting(false)
          },
      }); 

  
    const handleAlertClose = () => {
        passwordChangeState.reset()
    }

    return <PasswordChangeForm error={passwordChangeState.isError || respondChallengeState.isError} onErrorClose={handleAlertClose} errorMsg={passwordChangeState.error?.message || respondChallengeState.error?.message} formik={formik} />;
  }

export default PasswordChangeFormContainer

